import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, FormControl, IconButton, InputLabel, MenuItem,
  Paper, Select, Stack, Tooltip, Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight, Fullscreen, FullscreenExit, PlayArrow, Slideshow } from '@mui/icons-material';
import { Navigate } from 'react-router-dom';
import Terminal from '../components/Terminal';
import { useAuth } from '../context/AuthContext';
import { isAdminEmail, loadAdminScenarios } from '../services/admin';

export default function PresentationPage() {
  const { currentUser } = useAuth();
  const isAdmin = isAdminEmail(currentUser?.email);
  const [scenarios, setScenarios] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [terminalStarted, setTerminalStarted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState('');
  const presentationRef = useRef(null);
  const terminalRef = useRef(null);
  const pendingCommandsRef = useRef([]);
  const sessionSlug = useMemo(() => String(currentUser?.uid || currentUser?.email || 'presentation'), [currentUser]);
  const selectedRecord = scenarios.find((item) => item.scenarioId === selectedId);
  const scenario = selectedRecord?.scenarioPayload;
  const steps = Array.isArray(scenario?.steps) ? scenario.steps : [];
  const currentStep = steps[activeStep];

  useEffect(() => {
    if (!isAdmin) return;
    loadAdminScenarios().then((data) => {
      const loaded = data.scenarios || [];
      setScenarios(loaded);
      setSelectedId((current) => current || loaded[0]?.scenarioId || '');
    }).catch((err) => setError(err.message || 'Failed to load scenarios.'));
  }, [isAdmin]);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    if (!terminalStarted || !terminalRef.current) return;
    pendingCommandsRef.current.splice(0).forEach((command) => terminalRef.current?.executeCommand(command));
  }, [terminalStarted]);

  if (!isAdmin) return <Navigate to="/" replace />;

  const selectScenario = (id) => {
    pendingCommandsRef.current = [];
    setSelectedId(id);
    setActiveStep(0);
    setTerminalStarted(false);
  };
  const executeCommands = (commands) => {
    const validCommands = (Array.isArray(commands) ? commands : [commands])
      .map((command) => typeof command === 'string' ? command.trim() : command?.command?.trim())
      .filter(Boolean);
    if (!validCommands.length || selectedRecord?.enabled === false) return;

    pendingCommandsRef.current.push(...validCommands);
    setTerminalStarted(true);
    // Let React mount the terminal before forwarding the queued commands. The
    // terminal itself retains them until its websocket is ready.
    window.setTimeout(() => {
      if (terminalRef.current) {
        pendingCommandsRef.current.splice(0).forEach((command) => terminalRef.current.executeCommand(command));
      }
    }, 0);
  };
  const selectStep = (step, index) => {
    setActiveStep(index);
    executeCommands(step.commands || step.command);
  };
  const toggleFullscreen = async () => {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await presentationRef.current?.requestFullscreen();
  };

  return (
    <Box ref={presentationRef} sx={{ bgcolor: 'background.default', minHeight: isFullscreen ? '100vh' : 'calc(100vh - 144px)', p: isFullscreen ? 2 : 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Slideshow color="primary" />
            <Typography variant="h4" component="h1">{scenario?.title || 'Presentation scenarios'}</Typography>
            <Chip label="Admin" size="small" color="primary" variant="outlined" />
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>{scenario?.description || 'Choose a database-backed scenario to begin.'}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel id="scenario-select-label">Scenario</InputLabel>
            <Select labelId="scenario-select-label" value={selectedId} label="Scenario" onChange={(event) => selectScenario(event.target.value)}>
              {scenarios.map((item) => <MenuItem key={item.scenarioId} value={item.scenarioId}>{item.title}{item.enabled ? '' : ' (disabled)'}</MenuItem>)}
            </Select>
          </FormControl>
          <Tooltip title={isFullscreen ? 'Exit full screen' : 'Enter full screen'}>
            <IconButton onClick={toggleFullscreen} aria-label={isFullscreen ? 'Exit full screen' : 'Enter full screen'}>{isFullscreen ? <FullscreenExit /> : <Fullscreen />}</IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '360px minmax(0, 1fr)' }, gap: 2, flex: 1, minHeight: 0 }}>
        <Paper variant="outlined" sx={{ p: 2, overflowY: 'auto' }}>
          <Typography variant="h6" gutterBottom>Scenario steps</Typography>
          <Stack spacing={1}>
            {steps.map((step, index) => (
              <Paper key={`${selectedId}-${index}`} variant="outlined" onClick={() => selectStep(step, index)} sx={{ p: 1.5, borderColor: index === activeStep ? 'primary.main' : 'divider', cursor: 'pointer' }}>
                <Stack direction="row" spacing={1} alignItems="center"><Chip label={index + 1} size="small" color={index === activeStep ? 'primary' : 'default'} /><Typography variant="subtitle1">{step.title}</Typography></Stack>
                <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>{step.description || step.achievement}</Typography>
                {Array.isArray(step.tasks) && step.tasks.length > 0 && (
                  <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                    {step.tasks.map((task, taskIndex) => (
                      <Button
                        key={task.id || `${index}-${taskIndex}`}
                        size="small"
                        variant="outlined"
                        startIcon={<PlayArrow />}
                        disabled={!task.command || selectedRecord?.enabled === false}
                        onClick={(event) => { event.stopPropagation(); setActiveStep(index); executeCommands(task.command); }}
                        sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                      >
                        {task.title || task.name || `Task ${taskIndex + 1}`}
                      </Button>
                    ))}
                  </Stack>
                )}
              </Paper>
            ))}
            {!steps.length && <Typography color="text.secondary">No scenario steps are available.</Typography>}
          </Stack>
        </Paper>

        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Paper variant="outlined" sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" disabled={activeStep === 0} onClick={() => selectStep(steps[activeStep - 1], activeStep - 1)} aria-label="Previous step"><ChevronLeft /></IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}><Typography variant="overline" color="primary.main">Step {steps.length ? activeStep + 1 : 0} of {steps.length}</Typography><Typography variant="h6" noWrap>{currentStep?.title || 'Select a scenario'}</Typography></Box>
            <IconButton size="small" disabled={activeStep >= steps.length - 1} onClick={() => selectStep(steps[activeStep + 1], activeStep + 1)} aria-label="Next step"><ChevronRight /></IconButton>
          </Paper>
          {!terminalStarted ? (
            <Paper variant="outlined" sx={{ flex: 1, minHeight: 480, display: 'grid', placeItems: 'center', bgcolor: '#0f172a', color: 'white' }}>
              <Stack spacing={2} alignItems="center" sx={{ maxWidth: 460, textAlign: 'center', p: 3 }}><Slideshow sx={{ fontSize: 56, color: 'primary.light' }} /><Typography variant="h4">Presentation terminal</Typography><Typography sx={{ color: '#cbd5e1' }}>{selectedRecord?.enabled === false ? 'This scenario is disabled. Enable it in Admin before starting.' : 'Start a clean terminal using the Docker image mapped to this scenario.'}</Typography><Button variant="contained" size="large" startIcon={<PlayArrow />} disabled={!selectedId || !steps.length || selectedRecord?.enabled === false} onClick={() => setTerminalStarted(true)}>Start scenario</Button></Stack>
            </Paper>
          ) : <Terminal ref={terminalRef} courseId={selectedId} sessionSlug={sessionSlug} height={isFullscreen ? 'calc(100vh - 174px)' : 'calc(100vh - 294px)'} />}
        </Box>
      </Box>
    </Box>
  );
}
